import { fechaHora, useTraduccion } from '@tallerya/i18n';
import { Boton } from '@tallerya/ui';
import type { UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';

// El historial de un registro (#181): la línea de tiempo de `190:9042`, la que
// estrenó la ficha de la orden (#141), para cualquier ficha que lo muestre.
//
//   ● Datos editados
//     Hoy 10:20 · Ana Batres
//     Precio de lista: 25,00 US$ → 30,00 US$
//
// Lo de alrededor —el título, su ⓘ— es del `PanelLateral` donde cada ficha la
// abre (#193), y la explicación del ⓘ, de cada una. Lo que cambia de una vista a otra es cómo se lee una entrada: cada una
// pasa su `texto`. La de las maestras está armada en `cambios.ts`; la orden tiene
// la suya, porque sus entradas tienen otra forma.

/** Lo que tiene toda entrada, de la orden o de una maestra. */
export interface EntradaBase {
  id: string;
  occurredAt: string;
  staffName?: string | null;
}

/** Cómo se lee una entrada: arriba qué pasó y, debajo, el detalle que haya. */
export interface TextoDeEntrada {
  titulo: string;
  /** Texto, o una pieza cuando lleva una etiqueta: el «Eliminado» de un valor que ya no está. */
  detalles?: readonly ReactNode[];
  /** El color del punto; sin él, gris. La orden lo pinta del estado. */
  color?: string | undefined;
}

export function LineaDeTiempo<T extends EntradaBase>({
  consulta,
  texto,
}: {
  consulta: UseQueryResult<readonly T[]>;
  texto: (entrada: T) => TextoDeEntrada;
}) {
  const { t, idioma } = useTraduccion();

  if (consulta.isPending) return <p className="ficha__nota">{t('app.cargando')}</p>;

  if (consulta.isError) {
    return (
      <div className="ficha__vacio">
        <p className="ficha__vacio-titulo">{t('ui.listado.error.titulo')}</p>
        <Boton variante="secundario" onClick={() => void consulta.refetch()}>
          {t('app.reintentar')}
        </Boton>
      </div>
    );
  }

  if (consulta.data.length === 0) return <p className="ficha__nota">{t('admin.historial.vacio')}</p>;

  return (
    <ol className="historial">
      {consulta.data.map((entrada) => {
        const { titulo, detalles = [], color } = texto(entrada);
        // Sin autor —antes de que la cuenta tuviera ficha, una ficha eliminada, la
        // plataforma—: se dice, antes que inventar uno.
        const quien = entrada.staffName ?? t('admin.historial.sinAutor');

        return (
          <li key={entrada.id} className="historial__entrada">
            <span className="historial__punto" style={color ? { background: color } : undefined} aria-hidden="true" />
            <div className="historial__cuerpo">
              <p className="historial__titulo">{titulo}</p>
              <p className="historial__detalle">
                {fechaHora(entrada.occurredAt, idioma)} · {quien}
              </p>
              {detalles.filter(Boolean).map((detalle, i) => (
                <p key={i} className="historial__detalle">
                  {detalle}
                </p>
              ))}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
